import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import AppHeader from '../navigation/AppHeader';
import MobileBottomNav from '../navigation/MobileBottomNav';
import DesktopSidebar from '../navigation/DesktopSidebar';
import StoryPickerSheet from '../navigation/StoryPickerSheet';
import FamilySelectorSheet from '../navigation/FamilySelectorSheet';
import CreateStoryModal from '../dashboard/CreateStoryModal';
import CreateFamilyModal from '../family/CreateFamilyModal';
import FamilyManagementModal from '../family/FamilyManagementModal';
import ProfileSettings from '../dashboard/ProfileSettings';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  showNavigation?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  showHeader = true,
  showNavigation = true
}) => {
  const { user } = useAuthStore();
  const { familyGroups } = useFamilyGroupStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const [showStoryPicker, setShowStoryPicker] = useState(false);
  const [showFamilySelector, setShowFamilySelector] = useState(false);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [managingFamilyId, setManagingFamilyId] = useState<string | null>(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  const handleToggleSidebar = () => {
    const newValue = !sidebarCollapsed;
    setSidebarCollapsed(newValue);
    localStorage.setItem('sidebarCollapsed', String(newValue));
  };

  const isAuthenticated = !!user;
  const showNav = showNavigation && isAuthenticated;

  // PRO-336: smart family-management open. If the user only has one family
  // (the common case after `Mi Familia` auto-creation on signup), skip the
  // selector sheet and open the management modal directly.
  const handleOpenFamilyManagement = () => {
    if (familyGroups.length === 1) {
      setManagingFamilyId(familyGroups[0].id);
    } else {
      setShowFamilySelector(true);
    }
  };

  // Custom-event bridge so deep components (Dashboard nudge, post-record
  // toast) can trigger the family-management flow without prop drilling.
  // Same pattern as the 'openRecordingOverlay' event used by MobileBottomNav.
  useEffect(() => {
    const onOpen = () => handleOpenFamilyManagement();
    window.addEventListener('openFamilyManagement', onOpen);
    return () => window.removeEventListener('openFamilyManagement', onOpen);
  }, [familyGroups.length]);

  // PRO-344: the dashboard filter chip always opens the SELECTOR sheet, even
  // with one family — its job is switching context, not managing members
  // (which is what openFamilyManagement special-cases to for 1-family users).
  useEffect(() => {
    const onOpenSelector = () => setShowFamilySelector(true);
    window.addEventListener('openFamilySelector', onOpenSelector);
    return () => window.removeEventListener('openFamilySelector', onOpenSelector);
  }, []);

  // PRO-344 iter 2: zero-families state — the dashboard pill jumps straight to
  // the create-family modal (an empty selector sheet would be a dead end).
  useEffect(() => {
    const onOpenCreate = () => setShowCreateFamily(true);
    window.addEventListener('openCreateFamily', onOpenCreate);
    return () => window.removeEventListener('openCreateFamily', onOpenCreate);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 font-sans overflow-x-clip">
      {showHeader && isAuthenticated && (
        <AppHeader
          onOpenProfile={() => setShowProfileSettings(true)}
        />
      )}

      <div className="flex">
        {showNav && (
          <DesktopSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
            onOpenStoryPicker={() => setShowStoryPicker(true)}
            onOpenFamilyManagement={() => setShowFamilySelector(true)}
          />
        )}

        <main className={`flex-1 relative overflow-x-hidden ${showNav ? `pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}` : ''}`} style={{ transition: 'margin-left 300ms' }}>
          {children}
        </main>
      </div>

      {showNav && (
        <MobileBottomNav
          onOpenStoryPicker={() => setShowStoryPicker(true)}
          onOpenFamilyManagement={handleOpenFamilyManagement}
        />
      )}

      <StoryPickerSheet
        isOpen={showStoryPicker}
        onClose={() => setShowStoryPicker(false)}
        onCreateStory={() => {
          setShowStoryPicker(false);
          setShowCreateStory(true);
        }}
      />

      <FamilySelectorSheet
        isOpen={showFamilySelector}
        onClose={() => setShowFamilySelector(false)}
        onCreateFamily={() => {
          setShowFamilySelector(false);
          setShowCreateFamily(true);
        }}
        onManageFamily={(familyId) => {
          setShowFamilySelector(false);
          setManagingFamilyId(familyId);
        }}
      />

      <CreateStoryModal
        isOpen={showCreateStory}
        onClose={() => setShowCreateStory(false)}
      />

      <CreateFamilyModal
        isOpen={showCreateFamily}
        onClose={() => setShowCreateFamily(false)}
      />

      {managingFamilyId && (
        <FamilyManagementModal
          isOpen={true}
          onClose={() => setManagingFamilyId(null)}
          familyGroupId={managingFamilyId}
        />
      )}

      <ProfileSettings
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />
    </div>
  );
};

export default Layout;
